// ===================================================
// ФАЙЛ: frontend/src/components/Layout/FavoritePagesTabs.jsx
// Компонент для отображения вкладок избранных страниц
// ===================================================
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, Store, Home, Truck, Users,
  BarChart2, RefreshCcw, Settings, Book, Webhook, DownloadCloud,
  Globe, DollarSign, Percent, FileText, Clock, Mail,
  CalendarDays, Database, UploadCloud, Sliders,
  Palette, ChevronLeft, ChevronRight, ShoppingCart, Bell,
  Image as ImageIcon, Network, QrCode, Calendar, Waypoints,
  Landmark, Search, Key
} from 'lucide-react';
import WayGptIcon from '../Common/WayGptIcon';
import { usePermissions } from 'hooks/usePermissions';
import { useAuth } from 'hooks/useAuth';
import { PERMISSIONS } from 'utils/constants';
import { api } from 'services';
import { pickSafeRussianBackendMessage, pickHttpStatusRussianFallback } from '../../utils/safeRussianBackendMessage';
import { toastAPI } from '../../contexts/ToastContext';
import {
  SCHEDULER_DASHBOARD_URL,
  BACKUP_DASHBOARD_URL,
  USERS_PORTAL_URL,
  WALLET_URL,
  BILLING_URL,
  DELGRAM_URL,
  MARKET_MODULE_URL,
  MARKET_CONNECTION_URL,
  MPLAZA_APP_CONNECTION_URL,
  MPLAZA_SELLER_CONNECTION_URL,
  CORE_MONITORING_DASHBOARD_URL,
} from '../../utils/externalLinks';
import { buildExternalModuleEntryUrl } from '../../utils/externalModuleEntry';
import { resolveActiveModulesNav } from '../../utils/resolveActiveModulesNav';
import { buildNavigationHubs, flattenNavigationItems, applyNavigationBadges } from 'config/navigation.registry';
import { isProtectedSystemCompanyId } from '../../utils/systemCompany';
import { useNavigationPreferences } from 'hooks/useNavigationPreferences';
import NavBadge from './NavBadge';

/**
 * Сопоставление пункта избранного с текущим URL (включая query, напр. /settings?tab=balance).
 * Внешние http(s) ссылки в «активные» не попадают.
 */
function routeMatchesFavorite(itemPath, pathname, searchNorm) {
  if (!itemPath || typeof itemPath !== 'string') return false;
  if (/^https?:\/\//i.test(itemPath)) return false;
  if (itemPath.includes('?')) {
    const [pathPart, queryPart] = itemPath.split('?');
    if (pathname !== pathPart) return false;
    const need = new URLSearchParams(queryPart);
    const have = new URLSearchParams(searchNorm || '');
    for (const [k, v] of need) {
      if (have.get(k) !== v) return false;
    }
    return true;
  }
  if (itemPath === pathname) return true;
  if (itemPath !== '/' && itemPath.length > 0 && pathname.startsWith(`${itemPath}/`)) return true;
  return false;
}

// Каталог избранного — SSOT navigation.registry (без дубля путей)
const FAVORITE_ICON_MAP = {
  dashboard: LayoutDashboard,
  shopping: ShoppingBag,
  apartment: Network,
  partition: Waypoints,
  book: Book,
  percentage: Percent,
  picture: ImageIcon,
  truck: Truck,
  clock: Clock,
  cloudDownload: DownloadCloud,
  home: Home,
  calendar: Calendar,
  qrcode: QrCode,
  cart: ShoppingCart,
  shop: Store,
  barChart: BarChart2,
  settings: Settings,
  fileText: FileText,
  api: Webhook,
  sync: RefreshCcw,
  team: Users,
  bell: Bell,
  key: Key,
  ruble: DollarSign,
  dollar: DollarSign,
  bank: Landmark,
  user: Users,
  folder: Database,
  search: Search,
  schedule: CalendarDays,
  global: Globe,
  bgColors: Palette,
  database: Database,
  cloudUpload: UploadCloud,
  control: Sliders,
  robot: WayGptIcon,
  messageSquare: Mail,
  thunder: Store,
  rss: Globe,
  link: Webhook,
  safetyCertificate: Settings,
  scissor: FileText,
  compliance: FileText,
  appstore: LayoutDashboard,
  mail: Mail,
};

function renderFavoriteIcon(iconKey) {
  const Icon = FAVORITE_ICON_MAP[iconKey] || LayoutDashboard;
  return <Icon size={16} strokeWidth={1.5} />;
}

const getAllMenuItems = (hasPermission, isSuperAdminFn, isCompanyInactive, activeModules, company, urls, platform, hubOverrides) => {
  const overrides =
    hubOverrides && typeof hubOverrides === 'object' && !Array.isArray(hubOverrides)
      ? hubOverrides
      : {};
  const hubs = applyNavigationBadges(
    buildNavigationHubs({
      urls: urls || {},
      permissions: {
        has: hasPermission,
        isSuperAdmin: typeof isSuperAdminFn === 'function' ? isSuperAdminFn : () => Boolean(isSuperAdminFn),
        PERMISSIONS,
      },
      flags: {
        hasCompany: Boolean(company?.id),
        isCompanyInactive,
        isVerifiedCompany:
          company?.verified === true
          || company?.verification_status === 'verified'
          || Boolean(company?.verified_at),
        companyId: company?.id || null,
        isSystemMarkbaseCompany: isProtectedSystemCompanyId(company?.id),
      },
      activeModules,
    }).map((hub) => {
      const ov = overrides[hub.id];
      return {
        ...hub,
        userQuickPicks: Array.isArray(ov?.quickPicks) ? ov.quickPicks.filter(Boolean) : null,
      };
    }),
    platform
  );
  return flattenNavigationItems(hubs).map((it) => ({
    key: it.key,
    icon: renderFavoriteIcon(it.iconKey),
    title: it.label,
    path: it.href,
    available: it.available !== false && !it.disabled,
    external: Boolean(it.external),
    badge: it.badge || null,
  }));
};

const FavoritePagesTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { company, isAuthenticated, isCheckingAuth } = useAuth();
  const { prefs } = useNavigationPreferences({ enabled: Boolean(isAuthenticated) });
  const authReady = isAuthenticated && !isCheckingAuth;
  const isCompanyInactive = company?.is_active === false;
  const superAdminUser = isSuperAdmin();
  const activeModules = useMemo(
    () => resolveActiveModulesNav(company, { isSuperAdmin: superAdminUser }),
    [company?.id, company?.settings?.modules, superAdminUser]
  );

  const [favorites, setFavorites] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragOverSide, setDragOverSide] = useState(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isOverlapping, setIsOverlapping] = useState(false);

  const tabsListRef = useRef(null);
  const settingsBtnRef = useRef(null);
  const dragOverThrottleRef = useRef(null);

  // Throttling для загрузки favorites
  const favoritesLoadedRef = useRef(false);
  const lastLoadFavoritesRef = useRef(0);
  const LOAD_FAVORITES_INTERVAL = 30000; // Минимум 30 секунд между запросами
  const menuItemsMapRef = useRef(null);

  // Получаем все элементы меню
  const allMenuItems = useMemo(() => {
    return getAllMenuItems(
      hasPermission,
      isSuperAdmin,
      isCompanyInactive,
      activeModules,
      company,
      {
        USERS_PORTAL_URL,
        WALLET_URL,
        BILLING_URL,
        SCHEDULER_DASHBOARD_URL,
        BACKUP_DASHBOARD_URL,
        DELGRAM_URL,
        MARKET_MODULE_URL,
        MARKET_CONNECTION_URL,
        MPLAZA_APP_CONNECTION_URL,
        MPLAZA_SELLER_CONNECTION_URL,
        CORE_MONITORING_DASHBOARD_URL,
      },
      prefs?.platform,
      prefs?.hubOverrides
    );
  }, [hasPermission, isSuperAdmin, isCompanyInactive, activeModules, company, prefs?.platform, prefs?.hubOverrides]);

  // Создаем Map для быстрого поиска элементов меню по ключу
  const menuItemsMap = useMemo(() => {
    const map = new Map();
    allMenuItems.forEach(item => {
      map.set(item.key, item);
      if (item.path && item.path !== item.key) map.set(item.path, item);
    });
    // Legacy favorites key drift: /orders → /modules/orders
    if (map.has('/modules/orders') && !map.has('/orders')) {
      map.set('/orders', map.get('/modules/orders'));
    }
    return map;
  }, [allMenuItems]);

  // Обновляем ref при изменении menuItemsMap
  useEffect(() => {
    menuItemsMapRef.current = menuItemsMap;
  }, [menuItemsMap]);

  // Загружаем избранные страницы после готовности UAM-сессии
  useEffect(() => {
    if (!authReady) return undefined;

    const loadFavorites = async () => {
      const now = Date.now();
      if (favoritesLoadedRef.current && lastLoadFavoritesRef.current > 0
          && (now - lastLoadFavoritesRef.current < LOAD_FAVORITES_INTERVAL)) {
        return;
      }

      try {
        const favoriteData = await api.users.getMenuFavorites();
        const sortedFavorites = favoriteData.sort((a, b) => (a.order || 0) - (b.order || 0));
        setFavorites(sortedFavorites);
        favoritesLoadedRef.current = true;
        lastLoadFavoritesRef.current = now;
      } catch (error) {
        if (error?.response?.status === 401) return;
        toastAPI.error(
          pickSafeRussianBackendMessage(
            error?.response?.data?.error ?? error?.response?.data?.message,
            pickHttpStatusRussianFallback(error?.response?.status)
          )
        );
      }
    };

    loadFavorites();

    const onAuthReady = () => loadFavorites();
    window.addEventListener('markbase_auth_ready', onAuthReady);
    return () => window.removeEventListener('markbase_auth_ready', onAuthReady);
  }, [authReady]);

  // Живое обновление полосы вкладок после правок избранного на /menu-guide.
  useEffect(() => {
    const reload = async () => {
      try {
        const favoriteData = await api.users.getMenuFavorites();
        const sorted = (favoriteData || []).sort((a, b) => (a.order || 0) - (b.order || 0));
        setFavorites(sorted);
        favoritesLoadedRef.current = true;
        lastLoadFavoritesRef.current = Date.now();
      } catch (_) {
        // тихо — список останется прежним
      }
    };
    window.addEventListener('menu_favorites_changed', reload);
    return () => window.removeEventListener('menu_favorites_changed', reload);
  }, []);

  // Фильтруем favorites при изменении menuItemsMap (без повторной загрузки из API)
  useEffect(() => {
    if (!favoritesLoadedRef.current) return;

    setFavorites(prevFavorites => {
      return prevFavorites.filter(fav => {
        const menuItem = menuItemsMap.get(fav.menuKey);
        return menuItem && menuItem.available;
      });
    });
  }, [menuItemsMap]); // Фильтруем только при изменении menuItemsMap

  // Сохраняем порядок избранных страниц
  const saveOrder = async (newFavorites) => {
    try {
      const favoritesWithOrder = newFavorites.map((fav, index) => ({
        menuKey: fav.menuKey,
        order: index
      }));
      await api.users.updateMenuFavoritesOrder(favoritesWithOrder);
      setFavorites(newFavorites);
    } catch (error) {
      toastAPI.error(
        pickSafeRussianBackendMessage(
          error?.response?.data?.error ?? error?.response?.data?.message,
          pickHttpStatusRussianFallback(error?.response?.status)
        )
      );
    }
  };

  // Обработка начала перетаскивания
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
  };

  // Обработка перетаскивания над элементом (оптимизировано с throttling)
  const handleDragOver = useCallback((e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';

    if (draggedIndex === null || draggedIndex === index) {
      if (dragOverIndex !== null || dragOverSide !== null) {
        setDragOverIndex(null);
        setDragOverSide(null);
      }
      return;
    }

    // Throttling для оптимизации производительности
    if (dragOverThrottleRef.current) {
      return;
    }

    // Сохраняем значения до вызова requestAnimationFrame
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX;
    const elementCenter = rect.left + rect.width / 2;
    const side = mouseX < elementCenter ? 'left' : 'right';
    const insertIndex = side === 'left' ? index : index + 1;

    dragOverThrottleRef.current = requestAnimationFrame(() => {
      // Обновляем только если изменилось
      setDragOverIndex(prev => prev !== insertIndex ? insertIndex : prev);
      setDragOverSide(prev => prev !== side ? side : prev);
      dragOverThrottleRef.current = null;
    });
  }, [draggedIndex, dragOverIndex, dragOverSide]);

  // Обработка окончания перетаскивания
  const handleDragEnd = () => {
    // Отменяем pending animation frame
    if (dragOverThrottleRef.current) {
      cancelAnimationFrame(dragOverThrottleRef.current);
      dragOverThrottleRef.current = null;
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  // Обработка отпускания элемента
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      setDragOverSide(null);
      return;
    }

    // Используем dragOverIndex для точной позиции вставки
    const insertIndex = dragOverIndex !== null ? dragOverIndex : dropIndex;

    const newFavorites = [...favorites];
    const draggedItem = newFavorites[draggedIndex];
    newFavorites.splice(draggedIndex, 1);

    // Корректируем индекс вставки если элемент удален перед целевой позицией
    const finalInsertIndex = draggedIndex < insertIndex ? insertIndex - 1 : insertIndex;
    newFavorites.splice(finalInsertIndex, 0, draggedItem);

    saveOrder(newFavorites);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setDragOverSide(null);
  };

  // Обработка выхода из области перетаскивания
  const handleDragLeave = (e) => {
    e.preventDefault();
    // Проверяем, что мы действительно вышли из элемента
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      // Не сбрасываем здесь, чтобы сохранить плавность
    }
  };

  // Обработка клика по вкладке
  const handleTabClick = (menuKey) => {
    const menuItem = menuItemsMap.get(menuKey);
    if (menuItem && menuItem.available) {
      if (typeof menuItem.path === 'string' && /^https?:\/\//i.test(menuItem.path)) {
        window.open(buildExternalModuleEntryUrl(menuItem.path), '_blank', 'noopener,noreferrer');
        return;
      }
      navigate(menuItem.path);
    }
  };

  // Активная вкладка: с учётом query ( /settings?tab=balance ); при нескольких совпадениях — самый длинный путь
  const activeKey = useMemo(() => {
    const pathname = location.pathname;
    const searchNorm = (location.search || '').replace(/^\?/, '');
    const candidates = [];
    favorites.forEach((fav) => {
      const menuItem = menuItemsMap.get(fav.menuKey);
      if (!menuItem || typeof menuItem.path !== 'string') return;
      if (/^https?:\/\//i.test(menuItem.path)) return;
      if (routeMatchesFavorite(menuItem.path, pathname, searchNorm)) {
        candidates.push({ menuKey: fav.menuKey, spec: (menuItem.path || '').length });
      }
    });
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => b.spec - a.spec);
    return candidates[0].menuKey;
  }, [location.pathname, location.search, favorites, menuItemsMap]);

  // Проверка возможности скролла
  const checkScrollability = useCallback(() => {
    const list = tabsListRef.current;
    if (!list) return;

    setCanScrollLeft(list.scrollLeft > 0);
    setCanScrollRight(list.scrollLeft < list.scrollWidth - list.clientWidth - 1);

    // Проверяем перекрытие кнопки настроек
    if (settingsBtnRef.current && list) {
      const listRect = list.getBoundingClientRect();
      const lastItem = list.lastElementChild;

      if (lastItem) {
        const lastItemRect = lastItem.getBoundingClientRect();
        // Проверяем, перекрывает ли кнопка последний элемент
        setIsOverlapping(lastItemRect.right > listRect.right);
      }
    }
  }, []);

  // Скролл влево
  const scrollLeft = () => {
    const list = tabsListRef.current;
    if (list) {
      list.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  // Скролл вправо
  const scrollRight = () => {
    const list = tabsListRef.current;
    if (list) {
      list.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Отслеживание скролла
  useEffect(() => {
    const list = tabsListRef.current;
    if (!list) return;

    checkScrollability();

    const handleScroll = () => {
      checkScrollability();
    };

    list.addEventListener('scroll', handleScroll);

    // Проверяем при изменении размера окна
    const handleResize = () => {
      checkScrollability();
    };

    window.addEventListener('resize', handleResize);

    // Проверяем при изменении избранных
    const observer = new MutationObserver(() => {
      checkScrollability();
    });

    observer.observe(list, { childList: true, subtree: true });

    return () => {
      list.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [favorites, checkScrollability]);

  // Если нет избранных страниц, не показываем компонент (включая кнопку настроек)
  if (favorites.length === 0) {
    return null;
  }

  return (
    <div className="favorite-pages-tabs-container">
      <nav className="favorite-tabs-wrapper mb-fav-tabs" aria-label="Избранные разделы">
        {/* Кнопка скролла влево */}
        {canScrollLeft && (
          <button
            type="button"
            onClick={scrollLeft}
            className="favorite-tabs-scroll-btn favorite-tabs-scroll-btn--left"
            aria-label="Прокрутить вкладки влево"
          >
            <ChevronLeft size={16} aria-hidden />
          </button>
        )}

        <div
          ref={tabsListRef}
          role="tablist"
          className={`favorite-tabs-list mb-fav-tabs__track ${canScrollLeft ? 'has-scroll-left' : ''} ${canScrollRight ? 'has-scroll-right' : ''}`}
        >
          {favorites.map((favorite, index) => {
            const menuItem = menuItemsMap.get(favorite.menuKey);
            if (!menuItem) return null;

            const isActive = activeKey === favorite.menuKey;
            const isDragging = draggedIndex === index;

            // Показываем индикатор перед элементом, если вставка должна быть на этой позиции
            const showIndicatorBefore = dragOverIndex === index && draggedIndex !== index;
            const tabId = `fav-tab-${String(favorite.menuKey).replace(/[^a-zA-Z0-9-_]/g, '_')}`;

            return (
              <React.Fragment key={favorite.menuKey}>
                {/* Индикатор вставки перед элементом */}
                {showIndicatorBefore && (
                  <div className="favorite-tabs-drop-indicator" />
                )}
                <div
                  id={tabId}
                  role="tab"
                  tabIndex={0}
                  aria-selected={isActive}
                  className={`favorite-tab-item mb-fav-tabs__tab ${isActive ? 'active mb-fav-tabs__tab--active' : ''} ${isDragging ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDragEnd={handleDragEnd}
                  onDrop={(e) => handleDrop(e, index)}
                  onClick={() => handleTabClick(favorite.menuKey)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleTabClick(favorite.menuKey);
                    }
                  }}
                >
                  <span className="favorite-tab-icon" aria-hidden>{menuItem.icon}</span>
                  <span className="favorite-tab-title">{menuItem.title}</span>
                  <NavBadge type={menuItem.badge} />
                </div>
              </React.Fragment>
            );
          })}
          {/* Индикатор вставки в конец списка */}
          {dragOverIndex === favorites.length && draggedIndex !== null && (
            <div className="favorite-tabs-drop-indicator" />
          )}
        </div>

        {/* Кнопка скролла вправо */}
        {canScrollRight && (
          <button
            type="button"
            onClick={scrollRight}
            className="favorite-tabs-scroll-btn favorite-tabs-scroll-btn--right"
            aria-label="Прокрутить вкладки вправо"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        )}

        <button
          ref={settingsBtnRef}
          type="button"
          onClick={() => navigate('/menu-guide')}
          className={`favorite-tabs-settings-btn ${isOverlapping ? 'overlapping' : ''}`}
          aria-label="Настроить избранные страницы"
        >
          <Settings size={16} aria-hidden />
        </button>
      </nav>
    </div>
  );
};

export default FavoritePagesTabs;
